import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RichTextEditor from "./ReusableComponent/RichTextEditor";
import { fetchQuestionsForAssignment } from "../pages/services/assignmentService";
import AssignmentQuestionPanel from "./ReusableComponent/AssignmentQuestionPanel";
import "../styles/AddForm.css";
import "../styles/AddAssignment.css";
import "../styles/UpdateAssignment.css";
import "./ReusableComponent/AssignmentQuestionPanel.css";
import { fetchBatchOptions } from "../pages/services/filterOptionsService";

const DAY_ABBR = { Monday: "M", Tuesday: "T", Wednesday: "W", Thursday: "Th", Friday: "F", Saturday: "Sa", Sunday: "Su" };
const formatBatchLabel = (b) => {
  const days = (b.batchDays || []).map(d => DAY_ABBR[d] || d).join("");
  return `${b.batchTitle} | ${days} - ${b.batchTime}`;
};

const STATUSES = [
  { value: "Open",      label: "Open" },
  { value: "Closed",    label: "Closed" },
  { value: "Completed", label: "Completed" },
];

const DIFFICULTY_CLASS = {
  Easy: "aq-easy", Medium: "aq-medium", Hard: "aq-hard",
  "Very Hard": "aq-very-hard", Professional: "aq-professional",
};

const API_URL = process.env.REACT_APP_API_URL;

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const UpdateAssignment = () => {
  const { id: assignmentId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    assignmentTitle: "", assignmentDuration: "", assignmentDescription: "",
    batchId: "", assignmentStartTime: "", assignmentEndTime: "",
    assignmentAnalysisStartTime: "", assignmentAnalysisEndTime: "",
    assignmentAttachment: "", status: "",
  });

  const [enableAnalysis, setEnableAnalysis] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [errors, setErrors] = useState({});

  // Questions state
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const res = await fetch(`${API_URL}/api/assignments/${assignmentId}`);
        const response = await res.json();
        if (!res.ok || !response?.data) {
          navigate("/not-found", { replace: true });
          return;
        }
        const data = response.data;
        const [startTime, endTime] =
          typeof data.assignmentTime === "string" && data.assignmentTime.includes(" - ")
            ? data.assignmentTime.split(" - ")
            : ["", ""];
        const analysisRaw = data.assignmentAnalysisTime;
        const hasAnalysis = typeof analysisRaw === "string" && analysisRaw.includes(" - ") &&
          analysisRaw.replace(" - ", "").trim() !== "";
        const [startAnalysisTime, endAnalysisTime] = hasAnalysis ? analysisRaw.split(" - ") : ["", ""];
        setEnableAnalysis(hasAnalysis);

        setFormData({
          assignmentTitle: data.assignmentTitle || "",
          assignmentDuration: data.assignmentDuration || "",
          assignmentDescription: data.assignmentDescription || "",
          batchId: data.batch?._id || "",
          assignmentStartTime: startTime,
          assignmentEndTime: endTime,
          assignmentAnalysisStartTime: startAnalysisTime,
          assignmentAnalysisEndTime: endAnalysisTime,
          assignmentAttachment: data.assignmentAttachment || "",
          status: data.status || "",
        });

        // Load question objects for existing IDs
        const existingIds = Array.isArray(data.questionIds) ? data.questionIds
          : Array.isArray(data.questions) && typeof data.questions[0] === "string" ? data.questions
          : Array.isArray(data.questions) ? data.questions.map(q => q._id)
          : [];

        if (existingIds.length > 0) {
          const qObjects = await fetchQuestionsForAssignment(existingIds);
          setSelectedQuestions(qObjects);
        }
      } catch (err) {
        console.error("Error loading assignment:", err);
      }
    };

    const fetchBatches = async () => {
      try {
        const opts = await fetchBatchOptions();
        setBatches(opts.map(o => ({ _id: o.value, batchTitle: o.label })));
      } catch (err) {
        console.error("Error loading batches:", err);
      }
    };

    fetchAssignment();
    fetchBatches();
  }, [assignmentId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (name === "assignmentStartTime" || name === "assignmentEndTime") {
      setErrors(p => { const n = {...p}; delete n.assignmentTime; return n; });
    } else if (name === "assignmentAnalysisStartTime" || name === "assignmentAnalysisEndTime") {
      setErrors(p => { const n = {...p}; delete n.assignmentAnalysisTime; return n; });
    } else {
      setErrors(p => ({ ...p, [name]: "" }));
    }
  };

  const validate = () => {
    const e = {};
    if (!formData.assignmentTitle.trim())       e.assignmentTitle       = "Title is required";
    if (!formData.assignmentDescription?.replace(/<[^>]*>/g, "").trim()) e.assignmentDescription = "Description is required";
    if (!formData.batchId)                      e.batchId               = "Batch is required";
    if (!formData.status.trim())                e.status                = "Status is required";
    return e;
  };

  const handleSliderConfirm = (_ids, _removedIds, objects) => {
    setSelectedQuestions(objects);
  };

  const confirmRemoveQuestion = () => {
    if (!deleteTarget) return;
    setSelectedQuestions(prev => prev.filter(q => q._id !== deleteTarget));
    setDeleteTarget(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const payload = {
        ...formData,
        assignmentDuration: String(formData.assignmentDuration).trim(),
        assignmentTime: `${formData.assignmentStartTime} - ${formData.assignmentEndTime}`,
        assignmentAnalysisTime: enableAnalysis
          ? `${formData.assignmentAnalysisStartTime} - ${formData.assignmentAnalysisEndTime}`
          : "",
        questionIds: selectedQuestions.map(q => q._id),
      };
      delete payload.assignmentStartTime;
      delete payload.assignmentEndTime;
      delete payload.assignmentAnalysisStartTime;
      delete payload.assignmentAnalysisEndTime;

      const response = await fetch(`${API_URL}/api/assignments/${assignmentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Failed to update assignment");
      }

      navigate("/assignments", { state: { successMessage: "Assignment updated successfully!" } });
    } catch (err) {
      setErrorMessage(err.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assignments/${assignmentId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete assignment");
      navigate("/assignments", { state: { successMessage: "Assignment deleted successfully!" } });
    } catch (error) {
      console.error("Error deleting assignment:", error);
      alert("Error deleting assignment: " + error.message);
    }
  };

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Saving changes…</p>
        </div>
      )}

      {showDeletePopup && (
        <div className="uf-delete-overlay">
          <div className="uf-delete-modal">
            <p className="uf-delete-msg">Are you sure you want to delete this assignment?</p>
            <div className="uf-delete-actions">
              <button className="uf-cancel-btn" onClick={() => setShowDeletePopup(false)}>Cancel</button>
              <button className="uf-confirm-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="aq-del-overlay">
          <div className="aq-del-modal">
            <p>Remove this question from the assignment?</p>
            <div className="aq-del-actions">
              <button className="aq-cancel-btn" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="aq-confirm-del-btn" onClick={confirmRemoveQuestion}>Remove</button>
            </div>
          </div>
        </div>
      )}

      <AssignmentQuestionPanel
        open={sliderOpen}
        onClose={() => setSliderOpen(false)}
        onConfirm={handleSliderConfirm}
        existingIds={selectedQuestions.map(q => q._id)}
        existingObjects={selectedQuestions}
      />

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon amber">📝</div>
          <div className="af-header-text" style={{ flex: 1 }}>
            <h2 className="af-title">Update Assignment</h2>
            <p className="af-subtitle">Edit assignment details and scheduling</p>
          </div>
          <button type="button" className="uf-delete-trigger" onClick={() => setShowDeletePopup(true)}>
            Delete
          </button>
        </div>

        {successMessage && <div className="af-alert af-alert-success" style={{margin:"16px 32px 0"}}>{successMessage}</div>}
        {errorMessage   && <div className="af-alert af-alert-error"   style={{margin:"16px 32px 0"}}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            <div className="af-section">
              <p className="af-section-title">Assignment Information</p>
              <Field label="Title" required error={errors.assignmentTitle}>
                <input type="text" name="assignmentTitle" placeholder="e.g. React Hooks Exercise"
                  value={formData.assignmentTitle} onChange={handleChange}
                  className={errors.assignmentTitle ? "error-input" : ""} />
              </Field>
              <div className="af-row">
                <Field label="Duration" error={errors.assignmentDuration}>
                  <input type="text" name="assignmentDuration" placeholder="e.g. 2 hours"
                    value={formData.assignmentDuration} onChange={handleChange} />
                </Field>
                <Field label="Batch" required error={errors.batchId}>
                  <select name="batchId" value={formData.batchId} onChange={handleChange}
                    className={errors.batchId ? "error-input" : ""}>
                    <option value="">{batches.length === 0 ? "Loading…" : "Select batch"}</option>
                    {batches.map(b => <option key={b._id} value={b._id}>{formatBatchLabel(b)}</option>)}
                  </select>
                </Field>
              </div>
              <div className="af-field">
                <label className="af-label">Description<span className="af-required">*</span></label>
                <div className={`af-editor-wrap${errors.assignmentDescription ? " error-input" : ""}`}>
                  <RichTextEditor
                    value={formData.assignmentDescription}
                    onBlur={v => setFormData(p => ({ ...p, assignmentDescription: v }))}
                    toolbar="basic"
                    height={280}
                    placeholder="What should students do for this assignment…"
                  />
                </div>
                {errors.assignmentDescription && <span className="af-error">{errors.assignmentDescription}</span>}
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Assignment Window</p>
              <div className="af-field">
                <label className="af-label">Submission Window</label>
                <div className="af-time-row">
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Opens</label>
                    <input type="datetime-local" name="assignmentStartTime" value={formData.assignmentStartTime}
                      onChange={handleChange} className={errors.assignmentTime ? "error-input" : ""} />
                  </div>
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Closes</label>
                    <input type="datetime-local" name="assignmentEndTime" value={formData.assignmentEndTime}
                      onChange={handleChange} className={errors.assignmentTime ? "error-input" : ""} />
                  </div>
                </div>
                {errors.assignmentTime && <span className="af-error">{errors.assignmentTime}</span>}
              </div>

              <div className="af-field">
                <label className="aa-checkbox-label">
                  <input
                    type="checkbox"
                    className="aa-checkbox"
                    checked={enableAnalysis}
                    onChange={e => setEnableAnalysis(e.target.checked)}
                  />
                  Enable Analysis
                </label>
              </div>

              {enableAnalysis && (
                <div className="af-field">
                  <label className="af-label">Analysis Window <span style={{fontWeight:400,color:"#9ca3af",fontSize:"12px"}}>(optional)</span></label>
                  <div className="af-time-row">
                    <div className="af-field">
                      <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Opens</label>
                      <input type="datetime-local" name="assignmentAnalysisStartTime"
                        value={formData.assignmentAnalysisStartTime} onChange={handleChange} />
                    </div>
                    <div className="af-field">
                      <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Closes</label>
                      <input type="datetime-local" name="assignmentAnalysisEndTime"
                        value={formData.assignmentAnalysisEndTime} onChange={handleChange} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="af-section">
              <p className="af-section-title">Attachment & Status</p>
              <Field label="Attachment Link" error={errors.assignmentAttachment}>
                <input type="text" name="assignmentAttachment"
                  placeholder="Paste Google Drive / GitHub / Docs link… (optional)"
                  value={formData.assignmentAttachment} onChange={handleChange} />
              </Field>
              <Field label="Status" required error={errors.status}>
                <select name="status" value={formData.status} onChange={handleChange}
                  className={errors.status ? "error-input" : ""}>
                  <option value="">Select status…</option>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>

            {/* Questions */}
            <div className="af-section">
              <div className="aq-section-header">
                <p className="af-section-title" style={{margin:0}}>
                  Questions <span style={{fontWeight:400,color:"#9ca3af",fontSize:"12px",textTransform:"none",letterSpacing:0}}>({selectedQuestions.length})</span>
                </p>
                <button type="button" className="aq-add-btn" onClick={() => setSliderOpen(true)}>
                  + Add Questions
                </button>
              </div>

              {selectedQuestions.length === 0 ? (
                <div className="aq-empty">No questions attached. Click "Add Questions" to link questions to this assignment.</div>
              ) : (
                <div className="aq-table-wrap">
                  <table className="aq-table">
                    <thead>
                      <tr>
                        <th className="aq-idx">#</th>
                        <th>Question</th>
                        <th>Type</th>
                        <th>Difficulty</th>
                        <th>Topic</th>
                        <th>Subject</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuestions.map((q, i) => (
                        <tr key={q._id}>
                          <td className="aq-idx">{i + 1}</td>
                          <td><div className="aq-q-text" title={q.question}>{q.question}</div></td>
                          <td>{q.questionType}</td>
                          <td>
                            <span className={`aq-badge ${DIFFICULTY_CLASS[q.difficultyLevel] || ""}`}>
                              {q.difficultyLevel}
                            </span>
                          </td>
                          <td>{q.topic}</td>
                          <td>{q.subject}</td>
                          <td>
                            <button type="button" className="aq-del-btn" onClick={() => setDeleteTarget(q._id)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

          <div className="af-submit-row">
            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? "Saving…" : "Update Assignment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateAssignment;
